import {
	type Country,
	dateFormats,
	DateObject,
	type FanShopCheckoutOrder,
	useCountries,
	useIntl,
	useLogger
} from '@capsicum/common';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { placeOrderPayloadAtom } from '../../data-access';
import { type DocumentType, type FieldsObject, type FieldType, ORDER_FIELD_MAPPINGS } from '../../models/orders';
import { ProductTypes } from '../../models/product';
import { useProductCheckout } from '../useCheckout';
import { useCheckoutErrorHandler, type UseCheckoutErrorHandlerProps } from '../useCheckoutErrorHandler';
import { useOrderFields } from '../useOrderFields';
import { useProduct } from '../useProduct';

type UseOrderDetailsProps = {
	quantity: number;
	variantId: string;
} & Omit<UseCheckoutErrorHandlerProps, 'error'>;

export const useOrderDetails = ({ onFailedCheckout, quantity, variantId }: UseOrderDetailsProps) => {
	const { $t } = useIntl();
	const { format } = dateFormats;
	const { getCountryBy } = useCountries();
	const [countDownString, setCountDownString] = useState<string>('--:--');
	const logger = useLogger();
	const {
		allImages,
		getCategory,
		getMainImage,
		getSsuAmount,
		getTokenRequired,
		isFetching,
		isLoading: isLoadingProduct,
		refetchProduct,
		variant
	} = useProduct({
		variantId
	});

	const { getFieldValueWithFallback } = useOrderFields();

	const [fields, setFields] = useState<FieldsObject>();
	const [plannedOrder, setPlannedOrder] = useState<FanShopCheckoutOrder['plannedOrder']>({});
	const [currentFields, setCurrentFields] = useState<Array<FieldType>>();
	const [checkoutKey, setCheckoutKey] = useState<string>('');
	const [placeOrderPayload, setPlaceOrderPayload] = useAtom(placeOrderPayloadAtom);

	const { checkoutError, isLoading: isLoadingCheckOut, startCheckout } = useProductCheckout();

	useCheckoutErrorHandler({ error: checkoutError, onFailedCheckout });

	const isLoadingOrderDetails = isLoadingCheckOut || isLoadingProduct;

	const isAuction = useMemo(() => variant?.product?.redeemType === 'auction', [variant]);
	const isTicketType = variant?.product?.productType?.name === ProductTypes.Ticket;
	const isExperienceType = variant?.product?.productType?.name === ProductTypes.Experience;

	const currentStepRef = useRef(1);
	const isSubmitButton = Number(quantity) === Number(currentStepRef.current) || !(isTicketType || isExperienceType);

	const mainImage = useMemo(() => getMainImage(variant)?.url || '', [getMainImage, variant]);
	const mainBlurHash = useMemo(() => getMainImage(variant)?.blurHash || '', [getMainImage, variant]);
	const isPackageProduct = variant?.product?.isPackage;
	const shouldPrefillRedeemForm = (quantity > 1 && currentStepRef.current === 1) || quantity === 1;

	/* Converts the list fields to objects in this case issuingCountry and documentType to ensure having the same model */
	const convertListFieldsToObjects = (details: FieldsObject, key: string) => {
		if (!details[key]) {
			return details;
		}

		details[key] = details[key]!.map(field => {
			if (field.value && typeof field.value === 'string' && field.options?.values) {
				if (field.fieldCategory === ORDER_FIELD_MAPPINGS.issuingCountry) {
					const country = getCountryBy(field.value, 'iso2');

					if (country) {
						field.value = country;
					}
				} else if (field.fieldCategory === ORDER_FIELD_MAPPINGS.documentType) {
					const index = field.options?.values?.indexOf(field.value);

					if (index !== -1) {
						field.value = {
							id: index + 1,
							name: field.value
						} as DocumentType;
					}
				}
			}

			return field;
		});

		return details;
	};

	const fetchData = useCallback(async () => {
		if (placeOrderPayload && Object.keys(placeOrderPayload).length) {
			setPlannedOrder(placeOrderPayload.plannedOrder);
			const keys = Object.keys(placeOrderPayload?.deliveryDetails || []);
			const key = keys[keys.length - 1];
			const details = placeOrderPayload?.deliveryDetails;

			currentStepRef.current = keys.length;
			setCheckoutKey(key!);

			if (key && details) {
				setFields(convertListFieldsToObjects(details as FieldsObject, key));
			}

			return;
		}

		try {
			const resp = await startCheckout({
				productVariantId: variantId ?? '',
				quantity
			});

			if (resp.data?.deliveryDetails) {
				const key = Object.keys(resp?.data?.deliveryDetails)[0];
				const details = resp?.data?.deliveryDetails;

				setPlannedOrder(resp?.data?.plannedOrder);

				if (key && details) {
					currentStepRef.current = 1;
					setCheckoutKey(key);
					setFields(convertListFieldsToObjects(details as FieldsObject, key));
				}
			}
		} catch (error) {
			logger.warn('Error fetching checkout info: ' + JSON.stringify(error));
		}
	}, [quantity, startCheckout, variantId, logger]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const navigateOrders = (action: 'next' | 'prev') => {
		switch (action) {
			case 'prev':
				if (fields && Object.keys(fields).length) {
					if (currentStepRef.current >= 1) {
						const nextKey = Object.keys(fields)[currentStepRef.current - 2]!;

						setCheckoutKey(nextKey);
						currentStepRef.current -= 1;
					}
				}
				break;
			case 'next':
				if (fields && Object.keys(fields).length) {
					if (currentStepRef.current < Object.keys(fields).length) {
						const nextKey = Object.keys(fields)[currentStepRef.current]!;

						setCheckoutKey(nextKey);
						currentStepRef.current += 1;
					}
				}
				break;
			default:
				break;
		}
	};

	const getFieldName = (name: string): string => name.replace(/\s/g, '');

	const getDefaultValues = (allFields: Array<FieldType>, shouldApplyDefaults = true) => {
		const values: { [key: string]: Country | DocumentType | string } = {};

		allFields.forEach(field => {
			if (shouldApplyDefaults) {
				switch (field.fieldType) {
					case 'text':
					case 'email':
						values[field.id] = getFieldValueWithFallback(field);
						break;
					case 'datetime':
						values[field.id] = getFieldValueWithFallback(field, value =>
							value ? format(value as string, 'YYYY-MM-DD') : ''
						);
						break;
					case 'list':
						/* These checks are necessary because even though we convert list fields to objects in fetchData,
						 * when getFieldValueWithFallback falls back to user profile data, it returns a string.
						 * We need to convert these strings to the proper object types (Country or DocumentType). */
						if (field.options?.isCountryList) {
							values[field.id] = getFieldValueWithFallback(field, value => {
								if (value && typeof value === 'object' && 'iso2' in value && 'name' in value) {
									return value as Country;
								}

								if (typeof value === 'string') {
									return getCountryBy(value, 'iso2') || value;
								}

								return value;
							});
						} else if (field.options?.isDocumentTypeList) {
							values[field.id] = getFieldValueWithFallback(field, value => {
								if (value && typeof value === 'object' && 'id' in value && 'name' in value) {
									return value as DocumentType;
								}

								if (typeof value === 'string' && field.options?.values) {
									const index = field.options?.values?.indexOf(value);

									if (index !== -1) {
										return {
											id: index + 1,
											name: value
										} as DocumentType;
									}
								}

								return value;
							});
						}
						break;
					default:
						break;
				}
			} else {
				// TODO: This is a temporary solution to ensure the values for second ticket holder
				//  are applied correctly. Should be refactored
				//  It avoid the prefilling of the form for second product holder
				values[field.id] =
					field.fieldType === 'datetime'
						? field.value
							? format(field.value as string, 'YYYY-MM-DD')
							: ''
						: (field.value as Country | DocumentType | string);
			}
		});

		return values;
	};

	const texts = useMemo(
		() => ({
			error: $t('rewards.fanshop.orderDetails.error'),
			numberOfProducts: $t('rewards.fanshop.orderDetails.numberOfTickets', {
				from: currentStepRef.current,
				until: quantity
			}),
			redeem: isSubmitButton
				? isAuction
					? $t('rewards.rpAuctions.complete')
					: $t('rewards.fanshop.product.redeemButton')
				: isTicketType
					? $t('rewards.fanshop.orderDetails.nextTicket')
					: $t('rewards.fanshop.orderDetails.next'),
			required: $t('rewards.fanshop.orderDetails.required'),
			submit: $t('rewards.fanshop.orderDetails.submitForm'),
			titleDetails: isTicketType
				? $t('rewards.fanshop.orderDetails.ticketDetails')
				: $t('rewards.fanshop.orderDetails.details')
		}),
		[$t, quantity, isSubmitButton, currentStepRef.current, isTicketType, isAuction]
	);

	const getAgeFields = useCallback((freshFields: FieldsObject) => {
		if (!freshFields || Object.keys(freshFields).length === 0) return [];

		return Object.values(freshFields ?? {})
			.flat()
			.filter(field => field.fieldCategory === ORDER_FIELD_MAPPINGS.birthDate);
	}, []);

	const isAgeValidationPassed = useCallback(
		(freshFields: FieldsObject) => {
			return getAgeFields(freshFields)?.some(field => {
				if (!field.value) return false;
				const age = dateFormats.howOld(new DateObject(field.value as string));

				return age >= 18;
			});
		},
		[getAgeFields]
	);
	/**
	 * Determines if a ticket redemption is for an underage user based on birth date validation
	 * We pass freshFields as a parameter to ensure we are working with the most current field data, immediately bypassing the asynchronous nature of a setState call
	 * @param freshFields - The current fields data from the form
	 */
	const isUnderageTicketRedeem = useCallback(
		(freshFields: FieldsObject) =>
			isTicketType && getAgeFields(freshFields)?.length > 0 && !isAgeValidationPassed(freshFields),
		[isTicketType, getAgeFields, isAgeValidationPassed]
	);

	return {
		allImages,
		checkoutKey,
		countDownString,
		currentFields,
		currentStepRef,
		fetchData,
		fields,
		getCategory,
		getDefaultValues,

		getFieldName,

		getMainImage,
		getSsuAmount,
		getTokenRequired,
		isExperienceType,
		isFetching,
		isLoadingOrderDetails,
		isPackageProduct,
		isSubmitButton,
		isTicketType,
		isUnderageTicketRedeem,
		mainBlurHash,
		mainImage,
		navigateOrders,
		plannedOrder,
		refetchProduct,
		setCountDownString,
		setCurrentFields,
		setFields,
		setPlaceOrderPayload,
		shouldPrefillRedeemForm,

		texts,
		variant
	};
};
